import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthProvider.jsx";
import { useActiveStore } from "../lib/activeStore.js";
import { Weather } from "../components/Weather.jsx";
import { useFeatureEnabled } from "../lib/appConfig.js";
import {
  subscribeUnits, subscribeStore,
  chargeCycleStatus, daysSinceCheck, elapsedCharging,
  CHARGE_CYCLE,
} from "../lib/data.js";

// TV mode — designed for the showroom break-room display. No chrome,
// no nav, huge type, auto-rotates focus through the urgent units.
//
// Layout (1920×1080 typical):
//   ┌─────────────────────────────────────────────────────────────────┐
//   │ [Clock+date]   [Weather panel]            [Compliance %]        │
//   │ [Overdue] [Due Soon] [Charging]                                  │
//   │ ┌─────── On charge now (rail) ──────────────────────────────────┐│
//   │ │ Stock# · Model · 2h 14m elapsed   …                            ││
//   │ └────────────────────────────────────────────────────────────────┘│
//   │ ┌─────── Focus card (auto-rotates 12s) ─────────────────────────┐│
//   │ │ Stock# · Model · 42d since charge                              ││
//   │ │ Sticky note                                                    ││
//   │ └────────────────────────────────────────────────────────────────┘│
//   │ Auto-refresh · rotation marker                                   │
//   └─────────────────────────────────────────────────────────────────┘
export function TvMode() {
  const { profile } = useAuth();
  const activeStore = useActiveStore();
  const tvEnabled = useFeatureEnabled("tvMode");

  // Sub-feature gate — if SEVRIC/rooftop admin has disabled TV mode for
  // this store, the route renders a small "disabled" splash instead of
  // populating with data. We deliberately don't 404; we want the manager
  // to see WHY they can't open /tv if they hit it from a bookmark.
  if (!tvEnabled) {
    return (
      <main style={{ padding: "60px 40px", color: "var(--text)", textAlign: "center" }}>
        <div style={{
          maxWidth: 540, margin: "0 auto",
          padding: "44px 40px",
          background:
            "radial-gradient(80% 120% at 0% 0%, rgba(122,167,230,0.10), transparent 55%), " +
            "rgba(255,255,255,0.024)",
          border: "1px solid var(--line)", borderRadius: 22,
        }}>
          <div style={{ fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 14 }}>
            Battery Care · TV mode
          </div>
          <h1 style={{
            fontFamily: "'Crimson Pro','Times New Roman',serif",
            fontSize: 36, lineHeight: 1.05, letterSpacing: "-0.035em",
            fontWeight: 500, margin: "0 0 14px",
          }}>
            TV mode is <em>off</em>.
          </h1>
          <p style={{ fontSize: 14.5, color: "var(--text-muted)", lineHeight: 1.55, margin: 0 }}>
            A rooftop admin has disabled the showroom display for this store. Flip it back on under <a href="https://portal.sevric.com/store-settings.html" style={{ color: "var(--brand-cobalt)" }}>Store settings</a> → Battery Care → TV mode.
          </p>
        </div>
      </main>
    );
  }
  const [units, setUnits] = useState([]);
  const [store, setStore] = useState(null);
  const [focusIdx, setFocusIdx] = useState(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!profile?.tenantId || !activeStore) return;
    return subscribeUnits(
      { tenantId: profile.tenantId, storeId: activeStore },
      (u) => setUnits(u || []),
    );
  }, [profile?.tenantId, activeStore]);

  // Read the store doc so we can geo-locate the weather panel. If lat/lng
  // are stored on the doc we use them directly; otherwise we fall back to
  // the doc's zip (resolved by the Weather component); otherwise the
  // Weather component's built-in fallback.
  useEffect(() => {
    if (!activeStore) return;
    return subscribeStore(activeStore, (s) => setStore(s));
  }, [activeStore]);

  // Tick a clock for the charging elapsed time and the in-view "as of" line.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const { overdue, due, chargingUnits, compliancePct, total } = useMemo(() => {
    let o = 0, d = 0;
    const charging = [];
    for (const u of units) {
      const cc = chargeCycleStatus(u);
      if (cc === "charging")      charging.push(u);
      else if (cc === "overdue")  o++;
      else if (cc === "due")      d++;
    }
    const t = units.length;
    const inCycle = t - (o + d);
    // Sort charging units by longest-elapsed first (most likely to be done).
    charging.sort((a, b) => {
      const am = a.chargingStartedAt?.toMillis ? a.chargingStartedAt.toMillis() : 0;
      const bm = b.chargingStartedAt?.toMillis ? b.chargingStartedAt.toMillis() : 0;
      return am - bm;
    });
    return {
      overdue: o, due: d, chargingUnits: charging, total: t,
      compliancePct: t > 0 ? Math.round((inCycle / t) * 100) : 100,
    };
  }, [units]);

  // Focus rotation — most urgent first: overdue → due. Charging is in its
  // own rail (above) so we don't rotate through it here.
  const focusPool = useMemo(() => {
    const byUrgency = (cc) => (cc === "overdue" ? 2 : cc === "due" ? 1 : 0);
    return [...units]
      .map((u) => ({ u, cc: chargeCycleStatus(u), age: daysSinceCheck(u) ?? (u.daysOnLot ?? 0) }))
      .filter((x) => x.cc === "overdue" || x.cc === "due")
      .sort((a, b) => byUrgency(b.cc) - byUrgency(a.cc) || b.age - a.age);
  }, [units]);

  useEffect(() => {
    setFocusIdx(0);
    if (focusPool.length === 0) return;
    const id = setInterval(() => {
      setFocusIdx((i) => (i + 1) % focusPool.length);
    }, 12000);
    return () => clearInterval(id);
  }, [focusPool.length]);

  const focused = focusPool[focusIdx];

  const time = new Date(now).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const date = new Date(now).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  // Weather config — prefer explicit coords on the store doc, fall back to
  // the store's zip, fall back to the Weather component's default. We pass
  // the store's `name` (or storeId) as the label so the card knows where
  // it is — and so the manager doesn't see "Cincinnati, OH" hardcoded on
  // a Mason Rd display.
  const weatherProps = store?.lat && store?.lng
    ? { lat: store.lat, lng: store.lng, label: store.city ? `${store.city}, ${store.state || ""}`.trim().replace(/,\s*$/, "") : (store.name || activeStore) }
    : store?.zip
    ? { zip: String(store.zip) }
    : {};

  return (
    <main style={{ padding: "28px 36px 24px", color: "var(--text)" }}>
      {/* Top row — clock | weather | compliance. Equal columns, tight gap. */}
      <header style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr auto",
        gap: 28,
        alignItems: "flex-end",
        marginBottom: 22,
      }}>
        <div>
          <div style={{ fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 8 }}>
            SEVRIC · Battery Care · {store?.name || activeStore || profile?.storeId || "—"}
          </div>
          <div style={{ fontFamily: "'Crimson Pro', 'Times New Roman', serif", fontSize: 60, lineHeight: 1, letterSpacing: "-0.045em" }}>
            {time}
          </div>
          <div style={{ fontSize: 16, color: "var(--text-muted)", marginTop: 6 }}>{date}</div>
        </div>

        <div style={{
          background: "rgba(122,167,230,0.04)",
          border: "1px solid var(--accent-line)",
          borderRadius: 18,
          padding: "16px 20px",
          position: "relative",
          overflow: "hidden",
        }}>
          <div style={{ position: "absolute", top: 0, left: 16, right: 16, height: 1, background: "linear-gradient(90deg, transparent, rgba(122,167,230,0.5), transparent)" }} />
          <Weather {...weatherProps} size="lg" />
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 8 }}>
            Compliance
          </div>
          <div style={{
            fontFamily: "'Crimson Pro', 'Times New Roman', serif",
            fontSize: 96, lineHeight: 1, letterSpacing: "-0.045em",
            color: compliancePct >= 90 ? "var(--ok)" : compliancePct >= 70 ? "var(--warn)" : "var(--danger)",
          }}>
            {compliancePct}<span style={{ fontSize: 40, opacity: 0.6 }}>%</span>
          </div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 4 }}>
            {Math.max(0, total - overdue - due)} of {total} on schedule
          </div>
        </div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 22 }}>
        <TvStat label="Overdue"   value={overdue}              tone="crit" />
        <TvStat label="Due soon"  value={due}                  tone="warn" />
        <TvStat label="Charging"  value={chargingUnits.length} tone="hv"   />
      </div>

      {/* On-charge rail — all currently-charging units with live elapsed
          timers. Always visible when anything's charging so a manager can
          see the whole charger bank at a glance. */}
      {chargingUnits.length > 0 && (
        <section style={{
          background: "rgba(96,165,250,0.05)",
          border: "1px solid rgba(96,165,250,0.30)",
          borderRadius: 20,
          padding: "18px 22px",
          marginBottom: 22,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
            <div style={{ fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--info)" }}>
              On charge now · {chargingUnits.length}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-dim)" }}>
              Live elapsed time per unit
            </div>
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: chargingUnits.length === 1 ? "1fr" : "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 10,
          }}>
            {chargingUnits.map((u) => (
              <ChargingTile key={u.id} unit={u} now={now} />
            ))}
          </div>
        </section>
      )}

      {focused ? (
        <FocusCard entry={focused} now={now} />
      ) : (
        chargingUnits.length === 0 && (
          <div style={{
            padding: "60px 40px",
            background: "rgba(74,222,128,0.06)",
            border: "1px solid rgba(74,222,128,0.30)",
            borderRadius: 24,
            textAlign: "center",
          }}>
            <div style={{ fontFamily: "'Crimson Pro', 'Times New Roman', serif", fontSize: 56, lineHeight: 1, color: "var(--ok)", marginBottom: 8 }}>
              Line is clean.
            </div>
            <div style={{ fontSize: 18, color: "var(--text-muted)" }}>
              All {total} unit{total === 1 ? "" : "s"} are inside the {CHARGE_CYCLE.dueDays}-day cycle.
            </div>
          </div>
        )
      )}

      <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text-faint)", letterSpacing: "0.04em" }}>
        <span>Auto-refreshes live · focus rotates every 12 seconds</span>
        <span>{focusPool.length > 0 && <>Showing {focusIdx + 1} of {focusPool.length}</>}</span>
      </div>
    </main>
  );
}

function TvStat({ label, value, tone }) {
  const color =
    tone === "crit" ? "var(--danger)" :
    tone === "warn" ? "var(--warn)" :
    tone === "hv"   ? "var(--info)" : "var(--text)";
  const bg =
    tone === "crit" ? "rgba(124,41,53,0.06)" :
    tone === "warn" ? "rgba(251,191,36,0.05)" :
    tone === "hv"   ? "rgba(96,165,250,0.05)" : "var(--panel)";
  const border =
    tone === "crit" ? "rgba(124,41,53,0.30)" :
    tone === "warn" ? "rgba(251,191,36,0.30)" :
    tone === "hv"   ? "rgba(96,165,250,0.30)" : "var(--line)";
  return (
    <div style={{
      background: bg,
      border: `1px solid ${border}`,
      borderRadius: 18,
      padding: "18px 24px",
    }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 6 }}>{label}</div>
      <div style={{
        fontFamily: "'Crimson Pro', 'Times New Roman', serif",
        fontSize: 64, lineHeight: 0.95, letterSpacing: "-0.045em",
        color,
      }}>
        {value}
      </div>
    </div>
  );
}

// Single charging-unit tile — lives in the dedicated "On charge now" rail
// at the top of the focus area. Shows stock # / heading / elapsed time
// and ticks every second via the parent's `now` clock.
function ChargingTile({ unit, now }) {
  const heading = [unit.year, unit.make, unit.model].filter(Boolean).join(" ") || unit.stockNumber;
  // Recompute elapsed using the live clock so it ticks visibly.
  const startMs = unit.chargingStartedAt?.toMillis ? unit.chargingStartedAt.toMillis() : null;
  let elapsedText = "—";
  if (startMs) {
    const sec = Math.max(0, Math.floor((now - startMs) / 1000));
    const min = Math.floor(sec / 60);
    const hr  = Math.floor(min / 60);
    elapsedText = hr > 0 ? `${hr}h ${min % 60}m` : `${min}m`;
  }
  return (
    <div style={{
      background: "rgba(96,165,250,0.08)",
      border: "1px solid rgba(96,165,250,0.32)",
      borderRadius: 14,
      padding: "12px 14px",
      display: "flex",
      alignItems: "center",
      gap: 14,
    }}>
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="var(--info)"
           strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
           style={{ flexShrink: 0 }}>
        <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
            {unit.stockNumber || "—"}
          </span>
          <span style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em",
            color: "var(--info)",
          }}>
            {elapsedText}
          </span>
        </div>
        <div style={{ fontSize: 13, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {heading}
          {unit.lotPos && <span style={{ color: "var(--text-dim)" }}> · Lot {unit.lotPos}</span>}
        </div>
      </div>
    </div>
  );
}

function FocusCard({ entry, now }) {
  const u = entry.u;
  const cc = entry.cc;
  const ds = daysSinceCheck(u);
  const heading = [u.year, u.make, u.model, u.trim].filter(Boolean).join(" ") || u.stockNumber;
  const badge = cc === "overdue" ? { text: "Overdue", color: "var(--danger)", bg: "rgba(124,41,53,0.10)" }
              :                    { text: "Due Soon", color: "var(--warn)",   bg: "rgba(251,191,36,0.10)" };
  return (
    <div style={{
      padding: "30px 34px",
      background: "rgba(255,255,255,0.024)",
      border: "1px solid var(--line)",
      borderRadius: 24,
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: 0, left: 32, right: 32, height: 1,
        background: "linear-gradient(90deg, transparent, rgba(122,167,230,0.45), transparent)",
      }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 32, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 360px", minWidth: 0 }}>
          <div style={{ fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 8 }}>
            Focus · stock {u.stockNumber || "—"}
          </div>
          <div style={{ fontFamily: "'Crimson Pro', 'Times New Roman', serif", fontSize: 52, lineHeight: 1.05, letterSpacing: "-0.035em", marginBottom: 10 }}>
            {heading}
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <span style={{
              padding: "6px 16px", borderRadius: 999,
              background: badge.bg, color: badge.color,
              border: `1px solid ${badge.color}`,
              fontSize: 14, fontWeight: 700, letterSpacing: "0.04em",
            }}>
              {badge.text}
            </span>
            {u.lotPos && (
              <span style={{ fontSize: 14, color: "var(--text-muted)" }}>Lot {u.lotPos}</span>
            )}
          </div>
        </div>
        <div style={{ textAlign: "right", minWidth: 180 }}>
          <div style={{ fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--text-dim)", marginBottom: 8 }}>
            {ds != null ? "Since charge" : "On lot"}
          </div>
          <div style={{
            fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
            fontSize: 88, lineHeight: 1, letterSpacing: "-0.04em",
            color: badge.color,
          }}>
            {ds != null ? ds : (u.daysOnLot ?? 0)}
            <span style={{ fontSize: 28, opacity: 0.65, marginLeft: 4 }}>d</span>
          </div>
        </div>
      </div>
      {u.note && (
        <div style={{
          marginTop: 20,
          padding: "14px 18px",
          background: "rgba(251,191,36,0.06)",
          border: "1px solid rgba(251,191,36,0.22)",
          borderRadius: 14,
          fontSize: 16, color: "var(--text-muted)",
        }}>
          {u.note}
        </div>
      )}
    </div>
  );
}
